import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import useHttp from "../../hooks/use-http";
import { endPoints } from "../../config/end-points";
import { cuteToast } from "../UI/cute";
import cuteAlert from "../UI/cute";
import { authActions } from "../../store/auth";
import NotFound from "../404";
import "./apikeys.css";

const ApiKeysPage = () => {
  const [keys, setKeys] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [formData, setFormData] = useState({
    key_name: "",
    key_value: "",
    description: "",
  });

  const dispatch = useDispatch();
  const user_roles = useSelector((state) => state.auth.roles);
  const { sendRequest } = useHttp();

  const isSuperAdmin = user_roles && user_roles.role_id === 3;

  useEffect(() => {
    document.title = `${endPoints.title} | API Keys`;
    dispatch(authActions.setLoaderStatus({ status: false }));
  }, [dispatch]);

  const fetchKeys = () => {
    sendRequest(
      { url: endPoints.apiKeys, method: "GET" },
      (response) => {
        if (response.status === 200) {
          setKeys(response.data || []);
        }
      }
    );
  };

  useEffect(() => {
    if (isSuperAdmin) fetchKeys();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const openAddModal = () => {
    setEditingKey(null);
    setFormData({ key_name: "", key_value: "", description: "" });
    setShowModal(true);
  };

  const openEditModal = (key) => {
    sendRequest(
      { url: `${endPoints.apiKeys}/${key.id}`, method: "GET" },
      (response) => {
        if (response.status === 200) {
          setEditingKey(key);
          setFormData({
            key_name: response.data.key_name,
            key_value: response.data.key_value,
            description: response.data.description || "",
          });
          setShowModal(true);
        }
      }
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.key_name || !formData.key_value) {
      cuteToast({ type: "error", message: "Key name and value are required", timer: 3000, title: "Validation Error" });
      return;
    }

    const url = editingKey ? `${endPoints.apiKeys}/${editingKey.id}` : endPoints.apiKeys;
    const method = editingKey ? "PUT" : "POST";

    sendRequest({ url, method, body: formData }, (response) => {
      if (response.status === 200) {
        cuteToast({ type: "success", message: response.message, timer: 3000, title: "Success" });
        setShowModal(false);
        fetchKeys();
      } else {
        cuteToast({ type: "error", message: response.message, timer: 3000, title: "Error" });
      }
    });
  };

  const handleDelete = (key) => {
    cuteAlert({
      type: "question",
      title: "Delete API Key?",
      message: `Are you sure you want to delete "${key.key_name}"? This cannot be undone.`,
      confirmText: "Delete",
      cancelText: "Cancel",
    }).then((result) => {
      if (result === "confirm") {
        sendRequest(
          { url: `${endPoints.apiKeys}/${key.id}`, method: "DELETE" },
          (response) => {
            if (response.status === 200) {
              cuteToast({ type: "success", message: "API key deleted", timer: 3000, title: "Deleted" });
              fetchKeys();
            }
          }
        );
      }
    });
  };

  const toggleKeyStatus = (key) => {
    sendRequest(
      { url: `${endPoints.apiKeys}/${key.id}`, method: "PUT", body: { is_active: !key.is_active } },
      (response) => {
        if (response.status === 200) {
          cuteToast({ type: "success", message: `Key ${key.is_active ? "disabled" : "enabled"}`, timer: 3000, title: "Updated" });
          fetchKeys();
        }
      }
    );
  };

  if (!isSuperAdmin) return <NotFound />;

  return (
    <section className="ak-page">
      <div className="container">
        {/* Header */}
        <div className="ak-header">
          <div className="ak-header-left">
            <h2>API Keys</h2>
            <p>
              {keys.length} key{keys.length !== 1 ? "s" : ""} configured
              <span className="ak-shield-icon">
                <i className="bi bi-shield-lock"></i> Encrypted
              </span>
            </p>
          </div>
          <button className="ak-add-btn" onClick={openAddModal}>
            <i className="bi bi-plus-lg"></i> Add New Key
          </button>
        </div>

        {/* Content */}
        {keys.length === 0 ? (
          <div className="ak-empty">
            <div className="ak-empty-icon">
              <i className="bi bi-key"></i>
            </div>
            <h5>No API keys yet</h5>
            <p>Store your sensitive API keys securely with encryption.</p>
            <button className="ak-add-btn" onClick={openAddModal}>
              <i className="bi bi-plus-lg"></i> Add Your First Key
            </button>
          </div>
        ) : (
          <div className="ak-table-card">
            <table>
              <thead>
                <tr>
                  <th>Key Name</th>
                  <th>Value</th>
                  <th>Description</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {keys.map((key) => (
                  <tr key={key.id}>
                    <td>
                      <span className="ak-key-name">{key.key_name}</span>
                    </td>
                    <td>
                      <span className="ak-masked-value">{key.key_value_masked}</span>
                    </td>
                    <td>
                      <span className="ak-description">{key.description || "—"}</span>
                    </td>
                    <td>
                      <span className={`ak-status ak-status--${key.is_active ? "active" : "inactive"}`}>
                        {key.is_active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td>
                      <div className="ak-actions">
                        <button
                          className="ak-action-btn"
                          title="Edit"
                          onClick={() => openEditModal(key)}
                        >
                          <i className="bi bi-pencil"></i>
                        </button>
                        <button
                          className="ak-action-btn ak-action-btn--toggle"
                          title={key.is_active ? "Disable" : "Enable"}
                          onClick={() => toggleKeyStatus(key)}
                        >
                          <i className={`bi bi-toggle-${key.is_active ? "on" : "off"}`}></i>
                        </button>
                        <button
                          className="ak-action-btn ak-action-btn--danger"
                          title="Delete"
                          onClick={() => handleDelete(key)}
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Usage Guide */}
        <div className="ak-guide">
          <div className="ak-guide-header">
            <i className="bi bi-lightbulb"></i>
            <h6>Usage Guide</h6>
          </div>
          <div className="ak-guide-body">
            <p>Store API keys here instead of .env files. Use lowercase with underscores for naming:</p>
            <ul className="ak-guide-list">
              <li><code>mailgun_api_key</code> <span className="ak-guide-desc">Mailgun API</span></li>
              <li><code>mailgun_domain</code> <span className="ak-guide-desc">Mailgun Domain</span></li>
              <li><code>aws_access_key</code> <span className="ak-guide-desc">AWS Access Key</span></li>
              <li><code>aws_secret_key</code> <span className="ak-guide-desc">AWS Secret</span></li>
              <li><code>stripe_secret_key</code> <span className="ak-guide-desc">Stripe Secret</span></li>
            </ul>
          </div>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="ak-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="ak-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ak-modal-header">
              <h5>
                <i className="bi bi-key"></i>
                {editingKey ? "Edit API Key" : "Add New API Key"}
              </h5>
              <button className="ak-modal-close" onClick={() => setShowModal(false)}>
                &times;
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="ak-modal-body">
                <div className="ak-field">
                  <label className="ak-field-label">
                    Key Name <span className="ak-required">*</span>
                  </label>
                  <input
                    type="text"
                    className="ak-field-input ak-field-input--mono"
                    name="key_name"
                    value={formData.key_name}
                    onChange={handleInputChange}
                    placeholder="e.g., mailgun_api_key"
                    disabled={!!editingKey}
                    required
                  />
                  <div className="ak-field-hint">
                    <i className="bi bi-info-circle"></i>
                    Lowercase with underscores (e.g., mailgun_domain)
                  </div>
                </div>

                <div className="ak-field">
                  <label className="ak-field-label">
                    Key Value <span className="ak-required">*</span>
                  </label>
                  <input
                    type="text"
                    className="ak-field-input ak-field-input--mono"
                    name="key_value"
                    value={formData.key_value}
                    onChange={handleInputChange}
                    placeholder="Enter the API key value"
                    required
                  />
                  <div className="ak-field-hint">
                    <i className="bi bi-shield-lock"></i>
                    Encrypted before storing in database
                  </div>
                </div>

                <div className="ak-field">
                  <label className="ak-field-label">Description</label>
                  <input
                    type="text"
                    className="ak-field-input"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Brief description (optional)"
                  />
                </div>
              </div>
              <div className="ak-modal-footer">
                <button type="button" className="ak-btn-cancel" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="ak-btn-submit">
                  <i className={`bi bi-${editingKey ? "check-lg" : "plus-lg"}`}></i>
                  {editingKey ? "Update Key" : "Add Key"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
};

export default ApiKeysPage;
